import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Users, Globe, Award, Heart, Utensils, Brain, type LucideIcon } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

/* ─── Milestone data ────────────────────────────────────────── */
interface MilestoneItem {
  year: string;
  title: string;
  description: string;
  side: 'left' | 'right';
  accentColor: string;
  glowColor: string;
  Icon: LucideIcon;
  tag: string;
}

const milestones: MilestoneItem[] = [
  {
    year: '2019',
    title: 'Foundation & Social Impact',
    description: 'Started as a youth-driven NGO in Kerala with just five members, focusing on social reform through creative platforms like Yukta News and Thug Bells.',
    side: 'left',
    accentColor: '#efae00',
    glowColor: 'rgba(239,174,0,0.4)',
    Icon: Users,
    tag: 'NGO IMPACT',
  },
  {
    year: '2020',
    title: 'Digital Expansion & ISO Certification',
    description: 'Launched EWSELLS (online charity store), Webynose (digital systems), QRAIT flood emergency assistance, and the hunger relief initiative Hunger Hunt.',
    side: 'right',
    accentColor: '#3b82f6',
    glowColor: 'rgba(59,130,246,0.4)',
    Icon: Globe,
    tag: 'TECH FOCUS',
  },
  {
    year: '2021',
    title: 'Growth & Global Recognition',
    description: 'Expanded to 200+ active members. Launched Lakshya e-learning courses and received the Global NGO Expo 2021 Award for fundraising innovation.',
    side: 'left',
    accentColor: '#efae00',
    glowColor: 'rgba(239,174,0,0.4)',
    Icon: Award,
    tag: 'NGO IMPACT',
  },
  {
    year: '2022',
    title: 'Community Volunteering & Nature Campz',
    description: 'Established EW GP Nature Campz for eco-tourism and Help Ring, a 24/7 toll-free volunteering project providing emergency local assistance.',
    side: 'right',
    accentColor: '#3b82f6',
    glowColor: 'rgba(59,130,246,0.4)',
    Icon: Heart,
    tag: 'TECH FOCUS',
  },
  {
    year: '2024',
    title: 'Smile Community Kitchen',
    description: "Launched Smile Community Kitchen, a modern smart kitchen providing nutritious meals for everyone with the motto: 'a smile for every heart.'",
    side: 'left',
    accentColor: '#efae00',
    glowColor: 'rgba(239,174,0,0.4)',
    Icon: Utensils,
    tag: 'NGO IMPACT',
  },
  {
    year: '2025',
    title: 'Digital Transformation & GenAI',
    description: 'Evolved into Expectation Walkers GenAI Research & Cybersecurity Services. Focuses on AI-powered security, Voice of Million, and Smile AI Network Integrity.',
    side: 'right',
    accentColor: '#3b82f6',
    glowColor: 'rgba(59,130,246,0.4)',
    Icon: Brain,
    tag: 'TECH FOCUS',
  },
];

/* ─── Stars ─────────────────────────────────────────────────── */
const Stars: React.FC<{ color: string }> = ({ color }) => (
  <div className="flex gap-1 mt-4 justify-center">
    {[0, 1, 2].map((i) => (
      <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ))}
  </div>
);

/* ─── Main Section ──────────────────────────────────────────── */
const MilestonesSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const pinRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mobilePinRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mobileLineRef = useRef<HTMLDivElement>(null);

  // Calculate SVG road path logic (same as before)
  const count = milestones.length;
  const segH = 380;
  const totalH = count * segH + 80;
  const cx = 200;
  const amp = 130;

  let d = `M ${cx} 0`;
  for (let i = 0; i < count; i++) {
    const y1 = (i + 0.33) * segH;
    const y2 = (i + 0.67) * segH;
    const yEnd = (i + 1) * segH;
    const xSwing = i % 2 === 0 ? cx - amp : cx + amp;
    d += ` C ${xSwing} ${y1}, ${xSwing} ${y2}, ${cx} ${yEnd}`;
  }

  useEffect(() => {
    // 1. Check refs and strict mode cleanup setup
    if (!sectionRef.current) return;

    // 2. gsap.context scopes all animations to this component and automatically
    // reverts (cleans up) ALL ScrollTriggers when the component unmounts.
    const ctx = gsap.context(() => {
      
      // -- ROAD PROGRESS FILL --
      if (pathRef.current) {
        const path = pathRef.current;
        // MUST get total length for stroke-dasharray trick
        const length = path.getTotalLength();
        
        // Hide the path initially
        gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
        
        // Animate the path drawing on scroll
        gsap.to(path, {
          strokeDashoffset: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: 'bottom bottom',
            // scrub: 1 gives a 1-second lag smoothing effect so the progress 
            // fluidly chases the scroll position instead of jittering (scrub: true).
            scrub: 1, 
          }
        });
      }

      // -- MOBILE CONTINUOUS LINE PROGRESS --
      if (mobileLineRef.current) {
        gsap.to(mobileLineRef.current, {
          scaleY: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: mobileLineRef.current.parentElement,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1,
          }
        });
      }

      // -- PIN POP-IN ANIMATIONS (Desktop) --
      pinRefs.current.forEach((pin) => {
        if (!pin) return;
        gsap.fromTo(
          pin,
          { opacity: 0, scale: 0.8, y: 40 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: pin,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            }
          }
        );
      });

      // -- PIN POP-IN ANIMATIONS (Mobile) --
      mobilePinRefs.current.forEach((pin) => {
        if (!pin) return;
        gsap.fromTo(
          pin,
          { opacity: 0, scale: 0.8, y: 30 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.5,
            ease: 'back.out(1.5)',
            scrollTrigger: {
              trigger: pin,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            }
          }
        );
      });

    }, sectionRef);

    // Proper React + GSAP cleanup: kills all ScrollTriggers created above
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="milestones" className="relative w-full pt-24 md:pt-32 overflow-hidden bg-[#030712]">
      {/* Soft background glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/3 top-1/4 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10" style={{ background: '#efae00' }} />
        <div className="absolute right-1/3 top-2/3 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10" style={{ background: '#3b82f6' }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        
        {/* Heading */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2.5 mb-3">
            <span className="h-px w-8 bg-[#efae00]/70" />
            <p className="text-xs font-bold tracking-[0.28em] text-[#efae00] uppercase">
              A journey through time
            </p>
            <span className="h-px w-8 bg-[#efae00]/70" />
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-black uppercase leading-none tracking-tight text-white">
            Our Milestones
          </h2>
          <div className="mt-3 mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-[#efae00] to-[#3b82f6]" />
          <p className="mt-5 text-slate-300/60 text-sm max-w-lg mx-auto font-light">
            Twisting through social reform, tech innovation, &amp; GenAI — one milestone at a time.
          </p>
        </div>

        {/* ── Desktop zigzag ── */}
        <div className="hidden md:block relative" style={{ minHeight: totalH }}>
          
          {/* Scroll-driven winding road */}
          <div className="absolute inset-0 pointer-events-none flex justify-center" style={{ height: totalH }}>
            <svg width="400" height="100%" viewBox={`0 0 400 ${totalH}`} preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <mask id="gsap-road-mask">
                  <path
                    ref={pathRef}
                    d={d}
                    fill="none"
                    stroke="white"
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                </mask>
              </defs>
              
              {/* Base track (dim, low-opacity) */}
              <path
                d={d}
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="2"
                strokeDasharray="8 6"
                strokeLinecap="round"
              />
              
              {/* Progress layer (bright white glowing, dashed, animated via GSAP mask) */}
              <path
                d={d}
                fill="none"
                stroke="#ffffff"
                strokeWidth="3"
                strokeDasharray="8 6"
                strokeLinecap="round"
                filter="url(#glow)"
                mask="url(#gsap-road-mask)"
              />
            </svg>
          </div>

          <div className="relative flex flex-col">
            {milestones.map((item, index) => {
              const isLeft = item.side === 'left';
              return (
                <div key={item.year} className="relative flex items-end justify-between w-full" style={{ height: 380 }}>
                  
                  {/* Left slot */}
                  <div className="flex-1 flex justify-end pr-4 pb-10">
                    {isLeft && (
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-2xl font-black tracking-wide" style={{ color: item.accentColor }}>{item.year}</span>
                          <span className="ml-2 text-lg" style={{ color: item.accentColor }}>•</span>
                        </div>
                        {/* Pin Card Reference */}
                        <div ref={(el) => (pinRefs.current[index] = el)} className="relative opacity-0" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                          <PinContent item={item} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Spacer */}
                  <div className="w-16 shrink-0" />

                  {/* Right slot */}
                  <div className="flex-1 flex justify-start pl-4 pb-10">
                    {!isLeft && (
                      <div className="flex items-center gap-4">
                        {/* Pin Card Reference */}
                        <div ref={(el) => (pinRefs.current[index] = el)} className="relative opacity-0" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                          <PinContent item={item} />
                        </div>
                        <div>
                          <span className="mr-2 text-lg" style={{ color: item.accentColor }}>•</span>
                          <span className="text-2xl font-black tracking-wide" style={{ color: item.accentColor }}>{item.year}</span>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        </div>

        {/* ── Mobile single column (Aceternity-style continuous timeline) ── */}
        <div className="md:hidden relative pb-10">
          
          {/* Static track line */}
          <div className="absolute left-[34px] top-20 bottom-10 w-[2px] bg-gradient-to-b from-transparent via-[#1e293b] to-transparent" />
          
          {/* Animated progress track line */}
          <div 
            ref={mobileLineRef}
            className="absolute left-[34px] top-20 bottom-10 w-[2px] origin-top"
            style={{ transform: 'scaleY(0)', background: 'linear-gradient(to bottom, #efae00, #3b82f6, #efae00)' }}
          />

          {milestones.map((item, index) => (
            <div key={item.year} className="flex justify-start pt-10 relative">
              {/* Timeline Dot (Aceternity style) */}
              <div 
                className="absolute left-6 top-[68px] w-6 h-6 rounded-full border-2 bg-[#0f172a] flex items-center justify-center z-10" 
                style={{ borderColor: item.accentColor }}
              >
                <div 
                  className="w-2.5 h-2.5 rounded-full" 
                  style={{ backgroundColor: item.accentColor, boxShadow: `0 0 8px ${item.glowColor}` }} 
                />
              </div>

              {/* Mobile Card Content */}
              <div className="pl-16 pr-4 w-full">
                <div 
                  ref={(el) => (mobilePinRefs.current[index] = el)} 
                  className="rounded-2xl px-5 py-5 shadow-xl opacity-0 border backdrop-blur-md relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(10,15,30,0.95) 0%, rgba(10,15,30,0.85) 100%)',
                    borderColor: `${item.accentColor}40`,
                    boxShadow: `0 8px 32px ${item.glowColor}33`,
                  }}
                >
                  <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(135deg, ${item.accentColor}08 0%, transparent 60%)` }} />
                  <div className="flex justify-between items-center mb-3 relative z-10">
                    <span className="text-xl font-black" style={{ color: item.accentColor }}>{item.year}</span>
                    <item.Icon size={24} strokeWidth={1.8} color={item.accentColor} />
                  </div>
                  <h4 className="text-sm font-black text-white uppercase tracking-wide mb-2 relative z-10">{item.title}</h4>
                  <p className="text-slate-300/80 text-xs leading-relaxed relative z-10">{item.description}</p>
                  <Stars color={item.accentColor} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 flex flex-col items-center gap-2 pb-16">
          <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-[#efae00] to-[#3b82f6]" />
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400/40 font-semibold">
            Expectation Walkers · Smile Cyber Tech
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─── Pin Content Component (Stateless geometry) ────────────── */
const PinContent: React.FC<{ item: MilestoneItem }> = ({ item }) => (
  <>
    {/* 1. Icon circle badge */}
    <div
      className="relative z-10 flex items-center justify-center rounded-full border-4 shadow-xl"
      style={{
        width: 72, height: 72,
        background: 'rgba(10,14,26,0.95)',
        borderColor: item.accentColor,
        boxShadow: `0 0 28px ${item.glowColor}, 0 0 8px ${item.accentColor}`,
        marginBottom: -20,
      }}
    >
      <item.Icon size={28} strokeWidth={1.8} color={item.accentColor} />
    </div>

    {/* 2. Card body */}
    <div
      className="relative w-full rounded-2xl text-center px-5 pt-8 pb-5 shadow-2xl backdrop-blur-md border"
      style={{
        background: 'linear-gradient(160deg, rgba(10,15,30,0.97) 0%, rgba(10,15,30,0.90) 100%)',
        boxShadow: `0 16px 60px ${item.glowColor}44, 0 2px 0 rgba(255,255,255,0.05) inset`,
        borderColor: `${item.accentColor}35`,
        minWidth: 200, maxWidth: 240,
        borderRadius: '18px 18px 6px 6px',
      }}
    >
      <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ background: `linear-gradient(160deg, ${item.accentColor}10 0%, transparent 60%)` }} />
      <div className="absolute top-3 right-3">
        <span
          className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
          style={{
            background: 'rgba(0,0,0,0.25)',
            color: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          {item.tag}
        </span>
      </div>
      <h4 className="text-sm font-black text-white uppercase tracking-wide leading-snug mb-2 relative z-10">
        {item.title}
      </h4>
      <p className="text-slate-300/80 text-[11px] font-light leading-relaxed relative z-10">
        {item.description}
      </p>
      <Stars color={item.accentColor} />
    </div>

    {/* 3. Taper triangle */}
    <div
      style={{
        width: 0, height: 0,
        borderLeft: '22px solid transparent', borderRight: '22px solid transparent',
        borderTop: `22px solid ${item.accentColor}99`,
        filter: `drop-shadow(0 4px 8px ${item.glowColor})`,
        flexShrink: 0,
      }}
    />

    {/* 4. Thin stem */}
    <div
      style={{
        width: 3, height: 40,
        background: `linear-gradient(to bottom, ${item.accentColor}, ${item.accentColor}44)`,
        borderRadius: 4, flexShrink: 0,
      }}
    />

    {/* 5. Road ring */}
    <div className="relative flex items-center justify-center" style={{ width: 32, height: 32, flexShrink: 0 }}>
      <div className="absolute rounded-full border-2" style={{ inset: 2, borderColor: item.accentColor }} />
      <div className="relative rounded-full" style={{ width: 10, height: 10, background: item.accentColor, boxShadow: `0 0 12px ${item.glowColor}` }} />
    </div>
  </>
);

export default MilestonesSection;
